// Thin client for the user-settings + avatar endpoints. Bearer auth via the shell token.

export type AvatarType = 'initials' | 'mascot' | 'image'
export type LocationConsent = 'unset' | 'granted' | 'denied'

export interface UserSettings {
  fullName: string | null
  preferredName: string | null
  describes: string | null
  avatarType: AvatarType
  avatarMascot: string | null
  avatarR2Key: string | null
  avatarUrl: string | null   // signed serve URL for an image avatar (server-minted on GET)
  retentionDays: number | null  // per-user default artifact retention (days); null = policy default
  locationConsent: LocationConsent
  /** User-confirmed location shown everywhere in the UI. Coordinates are never displayed. */
  locationDisplayName: string | null
  locationCity: string | null
  locationRegion: string | null
  locationCountry: string | null
  locationCountryCode: string | null
  /** Deliberately coarse, two-decimal coordinates. */
  locationLatitude: number | null
  locationLongitude: number | null
  locationSource: 'browser' | 'manual' | null
  locationTimezone: string | null
  locationDatasetVersion: string | null
  locationConfirmedAt: string | null
  locationUpdatedAt: string | null
}

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

export async function fetchUserSettings(apiBaseUrl: string, token: string | null): Promise<UserSettings> {
  const res = await fetch(`${apiBaseUrl}/api/r3/user/settings`, { credentials: 'include', headers: authHeaders(token) })
  if (!res.ok) throw new Error(`settings ${res.status}`)
  const data = await res.json() as { settings: UserSettings }
  return data.settings
}

export interface UserSettingsPatch {
  fullName?: string | null
  preferredName?: string | null
  describes?: string | null
  avatarType?: AvatarType
  avatarMascot?: string | null
  retentionDays?: number | null
}

export async function patchUserSettings(apiBaseUrl: string, token: string | null, patch: UserSettingsPatch): Promise<UserSettings> {
  const res = await fetch(`${apiBaseUrl}/api/r3/user/settings`, {
    method: 'PATCH', credentials: 'include', headers: authHeaders(token), body: JSON.stringify(patch),
  })
  if (!res.ok) throw new Error(`settings patch ${res.status}`)
  const data = await res.json() as { settings: UserSettings }
  return data.settings
}

async function locationRequest<T>(
  apiBaseUrl: string,
  token: string | null,
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(`${apiBaseUrl}${path}`, {
    method: 'POST', credentials: 'include', headers: authHeaders(token), body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(data.error || `location ${res.status}`)
  }
  return await res.json() as T
}

export interface LocationResolution {
  displayName: string
  city: string
  region: string | null
  country: string
  countryCode: string | null
  timezone: string | null
  explanation: string
}

export interface NearbyPlace {
  city: string
  region: string | null
  country: string
  countryCode: string
  distanceKm: number
  population: number | null
  featureCode: string | null
}

export interface ResolvedLocationResult {
  resolution: LocationResolution
  coordinates: { latitude: number; longitude: number } | null
  nearbyPlaces: NearbyPlace[]
  datasetVersion: string | null
}

/** Round in the browser before the coordinate crosses the network; the Worker rounds again. Nothing is saved yet. */
export function resolveBrowserLocation(
  apiBaseUrl: string,
  token: string | null,
  latitude: number,
  longitude: number,
): Promise<ResolvedLocationResult> {
  const round = (value: number) => Math.round(value * 100) / 100
  return locationRequest(apiBaseUrl, token, '/api/r3/user/location/resolve', {
    latitude: round(latitude),
    longitude: round(longitude),
  })
}

/** Let Ros normalize a manually entered city/municipality before the user confirms it. */
export function resolveManualLocation(
  apiBaseUrl: string,
  token: string | null,
  manualLocation: string,
): Promise<ResolvedLocationResult> {
  return locationRequest(apiBaseUrl, token, '/api/r3/user/location/resolve', { manualLocation })
}

export function confirmUserLocation(
  apiBaseUrl: string,
  token: string | null,
  resolved: ResolvedLocationResult,
  source: 'browser' | 'manual',
): Promise<UserSettings> {
  return locationRequest<{ settings: UserSettings }>(apiBaseUrl, token, '/api/r3/user/location', {
    action: 'confirm',
    resolution: resolved.resolution,
    latitude: resolved.coordinates?.latitude,
    longitude: resolved.coordinates?.longitude,
    datasetVersion: resolved.datasetVersion,
    source,
  }).then(data => data.settings)
}

export function declineBrowserLocation(apiBaseUrl: string, token: string | null): Promise<UserSettings> {
  return locationRequest<{ settings: UserSettings }>(
    apiBaseUrl, token, '/api/r3/user/location', { action: 'deny' },
  ).then(data => data.settings)
}

export async function removeUserLocation(apiBaseUrl: string, token: string | null): Promise<UserSettings> {
  const res = await fetch(`${apiBaseUrl}/api/r3/user/location`, {
    method: 'DELETE', credentials: 'include', headers: authHeaders(token),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(data.error || `location ${res.status}`)
  }
  return ((await res.json()) as { settings: UserSettings }).settings
}

/** Upload a profile image (raw bytes + Content-Type). Returns the new signed avatar URL. */
export async function uploadAvatar(apiBaseUrl: string, token: string | null, file: File): Promise<{ avatarR2Key: string; avatarUrl: string }> {
  const headers: Record<string, string> = { 'Content-Type': file.type }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${apiBaseUrl}/api/r3/user/avatar`, { method: 'POST', credentials: 'include', headers, body: file })
  if (!res.ok) {
    const msg = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(msg.error || `avatar ${res.status}`)
  }
  return await res.json() as { avatarR2Key: string; avatarUrl: string }
}
